import * as Api from '@ViewModels';
import { KeyDateKind } from '@ViewModels';
import * as React from 'react';
import { AddUserIcon } from '../../../../components/svgs/icons/AddUserIcon';
import { ArchiveIcon } from '../../../../components/svgs/icons/ArchiveIcon';
import { AssigneeIcon } from '../../../../components/svgs/icons/AssigneeIcon';
import { AutomationsIcon } from '../../../../components/svgs/icons/AutomationsIcon';
import { ClockIcon } from '../../../../components/svgs/icons/ClockIcon';
import { EmailIcon } from '../../../../components/svgs/icons/EmailIcon';
import { ExportIcon } from '../../../../components/svgs/icons/ExportIcon';
import { KeyDateIcon } from '../../../../components/svgs/icons/KeyDateIcon';
import { MergeIcon } from '../../../../components/svgs/icons/MergeIcon';
import { PostageIcon } from '../../../../components/svgs/icons/PostageIcon';
import { PrivateVisibilityIcon } from '../../../../components/svgs/icons/PrivateVisibilityIcon';
import { PublicVisibilityIcon } from '../../../../components/svgs/icons/PublicVisibilityIcon';
import { TagIcon } from '../../../../components/svgs/icons/TagIcon';
import { TagRemoveIcon } from '../../../../components/svgs/icons/TagRemoveIcon';
import { TargetIcon } from '../../../../components/svgs/icons/TargetIcon';
import { TextingIcon } from '../../../../components/svgs/icons/TextingIcon';
import { TrashIcon } from '../../../../components/svgs/icons/TrashIcon';
import {
	brandPrimaryHover,
	brandSecondary,
	contentCalendarTintColor,
	destructive,
	header,
	success,
} from '../../../../styles/colors';
import { IActionButton } from './ActionBarButtonTypes';
import { styleSheet } from './styles';

interface UseActionButtonsParams {
	// Status indicators
	isActive: boolean;
	canKeepInTouch: boolean;
	canMerge: boolean;
	canFocus: boolean;
	canRestore: boolean;
	isArchived: () => boolean;

	// User/account conditions
	canShowAutomations: boolean;
	canShowCards: boolean;
	canSendTextingCampaign: boolean;

	// Action handlers
	onKeepInTouch: (frequency: number) => void;
	onSendMessage: () => void;
	onSendTextClicked: () => void;
	onSendCard: () => void;
	onFocusContacts: () => void;
	openConfirmationDialogModal: (visibility: 'admin' | 'all') => () => void;
	openContactOwnerModal: () => void;
	openContactConnectionModal: () => void;
	openMergeModal: () => void;
	onDeleteClicked: () => void;

	// Other action handlers
	addTagModal: {
		setIsOpen: (isOpen: boolean) => () => void;
		onRequestClose: () => void;
		isOpen: boolean;
	};
	removeTagModal: {
		setIsOpen: (isOpen: boolean) => () => void;
		onRequestClose: () => void;
		isOpen: boolean;
	};
	restoreModal: {
		setIsOpen: (isOpen: boolean) => () => void;
		onRequestClose: () => void;
		isOpen: boolean;
	};
	keepInTouchModal: {
		setIsOpen: (isOpen: boolean) => () => void;
		onRequestClose: () => void;
		isOpen: boolean;
	};
	onExportContactsButtonClicked: () => void;
	onExportContactsPolicyReportButtonClicked: () => void;
	setIsAutomationOpen: (value: boolean) => void;
	selectionState: Api.ESelectionState;
	filter?: Api.IBulkContactsRequest;
}

export const useActionButtons = (params: UseActionButtonsParams): IActionButton[] => {
	const {
		isActive,
		canKeepInTouch,
		canMerge,
		canFocus,
		canRestore,
		isArchived,
		canShowAutomations,
		canShowCards,
		canSendTextingCampaign,
		onSendMessage,
		onSendTextClicked,
		onSendCard,
		onFocusContacts,
		openConfirmationDialogModal,
		openContactOwnerModal,
		openContactConnectionModal,
		openMergeModal,
		onDeleteClicked,
		addTagModal,
		removeTagModal,
		restoreModal,
		keepInTouchModal,
		onExportContactsButtonClicked,
		onExportContactsPolicyReportButtonClicked,
		setIsAutomationOpen,
		selectionState,
		filter,
	} = params;

	// Check if filter has any tag filters (to enable "Remove Tag" button)
	const hasAnyTagFilters = React.useMemo(() => {
		if (!filter?.filter?.criteria) {
			return false;
		}

		// Check for tag criteria at the top level
		let tagCriteria = filter.filter.criteria.filter(c => c.property === Api.ContactFilterCriteriaProperty.Tag);

		// If not found at top level, check if there's a nested criteria with OR operator (common for tags)
		if (tagCriteria.length === 0) {
			filter.filter.criteria.forEach(criterion => {
				if (criterion.criteria && Array.isArray(criterion.criteria)) {
					const nestedTagCriteria = criterion.criteria.filter(
						c => c.property === Api.ContactFilterCriteriaProperty.Tag
					);
					tagCriteria = tagCriteria.concat(nestedTagCriteria);
				}
			});
		}

		return tagCriteria.length > 0;
	}, [filter]);

	return React.useMemo(() => {
		const buttons: IActionButton[] = [
			{
				key: 'email',
				icon: <EmailIcon fillColor='#EA8160' />,
				children: 'Email',
				onClick: onSendMessage,
				disabled: !isActive,
				styleClass: styleSheet.sendEmail,
				actionButtonType: 'standard',
			},
			{
				key: 'text',
				icon: <TextingIcon fill='#6E27A3' />,
				children: 'Text',
				onClick: onSendTextClicked,
				disabled: selectionState === 'none',
				styleClass: styleSheet.sendText,
				actionButtonType: 'standard',
				showWhen: () => canSendTextingCampaign,
			},
			{
				key: 'card',
				icon: <PostageIcon fillColor='#00AAE8' />,
				children: 'Card',
				onClick: onSendCard,
				disabled: !isActive,
				styleClass: styleSheet.sendCard,
				actionButtonType: 'standard',
				showWhen: () => canShowCards,
			},
			{
				key: 'tag',
				icon: <TagIcon fillColor={brandPrimaryHover} />,
				children: 'Tag',
				onClick: addTagModal.setIsOpen(true),
				disabled: !isActive,
				styleClass: styleSheet.tag,
				actionButtonType: 'standard',
			},
			{
				key: 'merge',
				icon: <MergeIcon />,
				children: 'Merge',
				onClick: openMergeModal,
				disabled: !canMerge,
				styleClass: styleSheet.merge,
				actionButtonType: 'standard',
			},
			{
				key: 'focusedView',
				icon: <TargetIcon />,
				children: 'Focused View',
				onClick: onFocusContacts,
				disabled: !canFocus,
				styleClass: styleSheet.focus,
				actionButtonType: 'standard',
			},
			{
				key: 'keepInTouch',
				icon: <ClockIcon fillColor={brandSecondary} />,
				children: 'Keep in touch',
				onClick: keepInTouchModal.setIsOpen(true),
				disabled: !canKeepInTouch,
				styleClass: styleSheet.keepInTouch,
				actionButtonType: 'dropdownTrigger',
			},
			{
				key: 'automations',
				icon: <AutomationsIcon fillColor={success} />,
				children: 'Automations',
				onClick: () => setIsAutomationOpen(true),
				disabled: !isActive,
				styleClass: styleSheet.export,
				actionButtonType: 'standard',
				showWhen: () => canShowAutomations,
			},
			{
				key: 'export',
				icon: <ExportIcon fillColor={success} />,
				children: 'Export',
				onClick: onExportContactsButtonClicked,
				disabled: !isActive,
				styleClass: styleSheet.export,
				actionButtonType: 'standard',
			},

			// Restore button for archived contacts
			{
				key: 'restore',
				icon: <ArchiveIcon />,
				children: 'Restore selected contacts',
				onClick: restoreModal.setIsOpen(true),
				disabled: !canRestore,
				styleClass: styleSheet.keepInTouch,
				actionButtonType: 'standard',
				showWhen: () => isArchived(),
			},

			// Always in More menu items
			{
				key: 'exportPolicies',
				icon: <KeyDateIcon kind={KeyDateKind.Renewal} fillColor={contentCalendarTintColor} />,
				children: 'Export Policies',
				onClick: onExportContactsPolicyReportButtonClicked,
				disabled: !isActive,
				styleClass: styleSheet.exportPolicies,
				actionButtonType: 'standard',
			},
			{
				key: 'visibilityAll',
				icon: <PublicVisibilityIcon />,
				children: 'Visibility: all employees',
				onClick: openConfirmationDialogModal('all'),
				disabled: !isActive,
				styleClass: styleSheet.visibilityAll,
				actionButtonType: 'standard',
			},
			{
				key: 'visibilityPrivate',
				icon: <PrivateVisibilityIcon />,
				children: 'Visibility: private',
				onClick: openConfirmationDialogModal('admin'),
				disabled: !isActive,
				styleClass: styleSheet.visibilityPrivate,
				actionButtonType: 'standard',
			},
			{
				key: 'removeTag',
				icon: <TagRemoveIcon fillColor={destructive} />,
				children: 'Remove Tag',
				onClick: removeTagModal.setIsOpen(true),
				disabled: !hasAnyTagFilters || !isActive,
				styleClass: styleSheet.removeTag,
				actionButtonType: 'standard',
			},
			{
				key: 'assignContactOwner',
				icon: <AssigneeIcon style={{ color: brandSecondary }} />,
				children: 'Assign Contact Owner',
				onClick: openContactOwnerModal,
				disabled: !isActive,
				styleClass: styleSheet.assignContactOwner,
				actionButtonType: 'standard',
			},
			{
				key: 'addConnection',
				icon: <AddUserIcon fillColor={brandSecondary} />,
				children: 'Add connection',
				onClick: openContactConnectionModal,
				disabled: !isActive,
				styleClass: styleSheet.addConnection,
				actionButtonType: 'standard',
			},
			{
				key: 'delete',
				icon: <TrashIcon fillColor={header} />,
				children: 'Delete',
				onClick: onDeleteClicked,
				disabled: !isActive,
				styleClass: styleSheet.defaultButtonStyles,
				actionButtonType: 'standard',
			},
		];

		return buttons;
	}, [
		addTagModal,
		removeTagModal,
		canFocus,
		canKeepInTouch,
		canMerge,
		canRestore,
		canShowAutomations,
		canShowCards,
		canSendTextingCampaign,
		hasAnyTagFilters,
		isActive,
		isArchived,
		keepInTouchModal,
		openMergeModal,
		onDeleteClicked,
		onExportContactsButtonClicked,
		onExportContactsPolicyReportButtonClicked,
		onFocusContacts,
		openConfirmationDialogModal,
		openContactConnectionModal,
		openContactOwnerModal,
		restoreModal,
		setIsAutomationOpen,
		onSendCard,
		onSendMessage,
		onSendTextClicked,
		selectionState,
	]);
};
